import {
  Car01Icon,
  Location01Icon,
  MapsLocation01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import type { EventLocation } from '@sunrise-ui/api/events';
import {
  Button,
  ButtonVariants,
  Colors,
  TextBold,
  TextReg,
  openMapsLocation,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  location?: EventLocation | null;
  isLoading?: boolean;
};

/**
 * EventLocationSection - Displays event location with map link
 *
 * Shows:
 * - Venue name
 * - Full address
 * - "View on Maps" button that opens Google Maps
 */
export function EventLocationSection({ location, isLoading = false }: Props) {
  const styles = useDynamicStyles(createStyles);

  // Build display address
  const displayAddress = useMemo(() => {
    if (!location) return '';
    const parts = [
      location.address,
      location.city,
      location.state,
      location.postal_code,
    ].filter(Boolean);
    return parts.join(', ');
  }, [location]);

  const handleViewOnMaps = () => {
    if (!location) return;
    openMapsLocation({
      name: location.name,
      address: location.address,
      city: location.city,
      state: location.state,
      postalCode: location.postal_code,
    });
  };

  // Don't render if no location
  if (!isLoading && !location) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <View style={styles.container}>
        <TextBold style={styles.sectionTitle}>Location</TextBold>
        <View style={styles.loadingContainer}>
          <TextReg style={styles.loadingText}>Loading location...</TextReg>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TextBold style={styles.sectionTitle}>Location</TextBold>

      <View style={styles.locationCard}>
        <HugeiconsIcon
          icon={Location01Icon}
          size={20}
          color={Colors.opacity50}
        />
        <View style={styles.locationInfo}>
          <TextBold style={styles.venueName}>{location?.name}</TextBold>
          {displayAddress && (
            <TextReg style={styles.address}>{displayAddress}</TextReg>
          )}
        </View>
      </View>

      {location?.parking_instructions && (
        <View style={styles.infoCard}>
          <HugeiconsIcon icon={Car01Icon} size={20} color={Colors.opacity50} />
          <View style={styles.infoContent}>
            <TextBold style={styles.infoLabel}>Parking</TextBold>
            <TextReg style={styles.infoText}>
              {location.parking_instructions}
            </TextReg>
          </View>
        </View>
      )}

      {location?.wayfinding_instructions && (
        <View style={styles.infoCard}>
          <HugeiconsIcon
            icon={MapsLocation01Icon}
            size={20}
            color={Colors.opacity50}
          />
          <View style={styles.infoContent}>
            <TextBold style={styles.infoLabel}>Wayfinding</TextBold>
            <TextReg style={styles.infoText}>
              {location.wayfinding_instructions}
            </TextReg>
          </View>
        </View>
      )}

      <View style={styles.buttonContainer}>
        <Button
          size="small"
          variant={ButtonVariants.WHITE}
          onPress={handleViewOnMaps}
        >
          <TextReg>View on Maps</TextReg>
        </Button>
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingHorizontal: isMobile ? 16 : 0,
      gap: isMobile ? 16 : 20,
      alignItems: 'flex-start',
    },
    sectionTitle: {
      fontSize: isMobile ? 20 : 24,
      color: Colors.text,
      textAlign: 'left',
    },
    locationCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
      paddingVertical: 12,
      paddingHorizontal: 16,
      width: '100%',
      maxWidth: 500,
    },
    locationInfo: {
      flex: 1,
      gap: 4,
    },
    venueName: {
      fontSize: isMobile ? 15 : 16,
      color: Colors.text,
    },
    address: {
      fontSize: isMobile ? 13 : 14,
      color: Colors.text,
      opacity: 0.7,
    },
    buttonContainer: {
      alignItems: 'flex-start',
    },
    loadingContainer: {
      alignItems: 'flex-start',
      padding: 20,
    },
    loadingText: {
      color: Colors.text,
      opacity: 0.7,
    },
    infoCard: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      backgroundColor: Colors.opacity05,
      borderRadius: 16,
      paddingVertical: 12,
      paddingHorizontal: 16,
      width: '100%',
      maxWidth: 500,
    },
    infoContent: {
      flex: 1,
      gap: 4,
    },
    infoLabel: {
      fontSize: isMobile ? 14 : 15,
      color: Colors.text,
    },
    infoText: {
      fontSize: isMobile ? 13 : 14,
      color: Colors.text,
      opacity: 0.7,
    },
  });
